import PageRecord from "models/view-models/page-record";
import { useCallback, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { useReadonlyContext } from "utilities/contexts/readonly/use-readonly-context";
import {
    SortItemMovement,
    useSortedRecords,
} from "utilities/hooks/domain/use-sorted-records";
import PageService, {
    PageMoveBodyParams,
} from "utilities/services/notebooks/page-service";

export interface UseSortPages {
    clearErrors: () => void;
    errors: string[];
    onPagesReordered: (updatedPages: PageRecord[]) => Promise<number[]>;
    sortedPages: PageRecord[];
}

export const useSortPages = (unsortedPages: PageRecord[]): UseSortPages => {
    const { patch: moveApi } = PageService.useMove();
    const { records, onRecordsMoved, resetRecordSort } =
        useSortedRecords<PageRecord>(unsortedPages, PageRecord);
    const [errors, setErrors] = useState<string[]>([]);
    const { isReadonly } = useReadonlyContext();

    const makeMoveApiCall = useCallback(
        async (move: PageMoveBodyParams): Promise<number[]> => {
            try {
                const moveResponse = await moveApi({}, move);

                if (moveResponse.result?.hasErrors()) {
                    throw new Error(
                        moveResponse.result?.listErrorMessages().join(";")
                    );
                }

                if (moveResponse.resultObject == null) {
                    throw new Error("No page IDs received on move.");
                }

                return moveResponse.resultObject;
            } catch (error) {
                const errorMessages =
                    error instanceof Error
                        ? error.message.split(";")
                        : ["An unknown error occurred."];
                setErrors(errorMessages);

                return resetRecordSort();
            }
        },
        [moveApi, resetRecordSort]
    );

    const movePages = useCallback(
        async (moves: PageMoveBodyParams[]): Promise<number[]> => {
            if (CollectionUtils.isEmpty(moves)) {
                return [];
            }

            let blockIds: number[] = [];

            for (const move of moves) {
                blockIds = await makeMoveApiCall(move);
            }

            return blockIds;
        },
        [makeMoveApiCall]
    );

    const onPagesReordered = useCallback(
        async (updatedPages: PageRecord[]): Promise<number[]> => {
            if (isReadonly) {
                return records
                    .map((r) => r.id)
                    .filter((id): id is number => id != null);
            }

            const diffs = onRecordsMoved(updatedPages);
            const moves = diffsToMoves(diffs);

            return await movePages(moves);
        },
        [isReadonly, movePages, onRecordsMoved, records]
    );

    const clearErrors = useCallback(() => setErrors([]), []);

    return {
        clearErrors: clearErrors,
        errors: errors,
        onPagesReordered: onPagesReordered,
        sortedPages: records,
    };
};

const diffsToMoves = (diffs: SortItemMovement[]): PageMoveBodyParams[] => {
    return diffs.map(diffToMove);
};

const diffToMove = (diff: SortItemMovement): PageMoveBodyParams => ({
    movingPageId: diff.movingRecordId,
    precedingPageId: diff.precedingRecordId,
});
